/**
 * V1 Shopflo SSO: keep localStorage `flo_refresh_token` (+ expiry) warm for
 * the Liquid iframe bridge. Missing/expired → clear Shopflo state and use the
 * guest SDK without touching the Surma account. Network failures never block
 * cart/checkout.
 */
(function () {
  "use strict";

  var DEFAULT_API_BASE = "https://dev.api.surma.in";
  var FLO_REFRESH_KEY = "flo_refresh_token";
  var FLO_REFRESH_EXPIRY_KEY = "flo_refresh_token_expiry";
  var SESSION_TOKEN_KEY = "surma_session_token";
  var WEB_SESSION_KEY = "surma_web_session:" + new URL(apiBaseUrl()).origin;
  var READY_EVENT = "surma:shopflo-refresh-ready";
  /** Treat token as expired this many ms early. */
  var EXPIRY_SKEW_MS = 5 * 60 * 1000;
  /** Cap wait before opening cart/checkout so the flow never soft-locks. */
  var ENSURE_TIMEOUT_MS = 2000;
  var HYDRATE_TTL_MS = 5 * 60 * 1000;
  var lastHydrateAt = 0;
  /** @type {Promise<object> | null} */
  var inFlight = null;

  function apiBaseUrl() {
    var fromConfig =
      (window.SurmaConfig && window.SurmaConfig.apiBaseUrl) ||
      (window.MinimogSettings && window.MinimogSettings.surmaApiBaseUrl);
    var base = (fromConfig || DEFAULT_API_BASE)
      .toString()
      .trim()
      .replace(/\/$/, "");
    return base || DEFAULT_API_BASE;
  }

  function readLocal(key) {
    try {
      var value = localStorage.getItem(key);
      return value && value.trim() ? value.trim() : null;
    } catch (e) {
      return null;
    }
  }

  function writeRefresh(auth, expiresAt) {
    try {
      if (!auth) {
        localStorage.removeItem(FLO_REFRESH_KEY);
        localStorage.removeItem(FLO_REFRESH_EXPIRY_KEY);
        return;
      }
      localStorage.setItem(FLO_REFRESH_KEY, auth);
      if (expiresAt != null && !Number.isNaN(Number(expiresAt))) {
        localStorage.setItem(FLO_REFRESH_EXPIRY_KEY, String(expiresAt));
      } else {
        localStorage.removeItem(FLO_REFRESH_EXPIRY_KEY);
      }
      window.dispatchEvent(new Event(READY_EVENT));
    } catch (e) {
      console.warn("[surma-checkout] could not write flo_refresh_token", e);
    }
  }

  /** Disable Shopflo SSO without clearing Surma identity/session state. */
  function clearUnavailableShopfloSsoState() {
    writeRefresh(null);
    try {
      localStorage.removeItem("flo_auth_token");
      localStorage.removeItem("flo_auth_token_expiry");
      sessionStorage.removeItem("flo_isShopfloSession");
      sessionStorage.removeItem("FLO_SSO_IS_LOGOUT");
    } catch (e) {
      /* ignore */
    }
  }

  function localExpiryMs() {
    var raw = readLocal(FLO_REFRESH_EXPIRY_KEY);
    if (!raw) return null;
    var n = Number(raw);
    return Number.isFinite(n) ? n : null;
  }

  function isLocalRefreshExpired() {
    var exp = localExpiryMs();
    if (exp == null) return false;
    return Date.now() >= exp - EXPIRY_SKEW_MS;
  }

  function hasUsableLocalRefresh() {
    var token = readLocal(FLO_REFRESH_KEY);
    if (!token) return false;
    return !isLocalRefreshExpired();
  }

  function authHeaders() {
    var headers = {
      Accept: "application/json",
      "X-Surma-Client": "web",
    };
    var bearer = readLocal(SESSION_TOKEN_KEY);
    if (bearer) {
      headers.Authorization = "Bearer " + bearer;
    }
    return headers;
  }

  function unwrapData(body) {
    if (body && typeof body === "object" && "data" in body) {
      return body.data;
    }
    return body;
  }

  function decodeShopfloSsoCapability(body) {
    var data = unwrapData(body);
    if (!data || typeof data !== "object" || Array.isArray(data)) {
      throw new Error("shopflo_auth_invalid_response");
    }
    var status = data.status;
    var expiresAt = data.expiresAt;
    var validExpiry =
      expiresAt === null ||
      (typeof expiresAt === "number" && Number.isFinite(expiresAt));

    if (
      status === "ok" &&
      typeof data.auth === "string" &&
      data.auth.trim() &&
      validExpiry
    ) {
      return {
        status: "ok",
        auth: data.auth.trim(),
        expiresAt: expiresAt,
      };
    }
    if (status === "missing" && data.auth === null && expiresAt === null) {
      return { status: "missing", auth: null, expiresAt: null };
    }
    if (status === "expired" && data.auth === null && validExpiry) {
      return { status: "expired", auth: null, expiresAt: expiresAt };
    }
    throw new Error("shopflo_auth_invalid_response");
  }

  function clearSurmaSessionFlags() {
    try {
      localStorage.removeItem(WEB_SESSION_KEY);
      localStorage.removeItem(SESSION_TOKEN_KEY);
      sessionStorage.removeItem("flo_isShopfloSession");
    } catch (e) {
      /* ignore */
    }
  }

  // Restore the scoped hint on storefront pages too, including after rollout
  // from the old shared key. Only a successful backend response can set it.
  function confirmSurmaSession() {
    if (readLocal(WEB_SESSION_KEY)) return;
    try {
      localStorage.setItem(WEB_SESSION_KEY, "1");
    } catch (e) {
      return;
    }
    window.dispatchEvent(new Event("surma:identity-changed"));
  }

  /**
   * @param {boolean} [force]
   * @returns {Promise<{ status: string, auth: string | null, expiresAt: number | null }>}
   */
  function hydrateShopfloRefresh(force) {
    if (
      !force &&
      Date.now() - lastHydrateAt < HYDRATE_TTL_MS &&
      hasUsableLocalRefresh()
    ) {
      return Promise.resolve({
        status: "ok",
        auth: readLocal(FLO_REFRESH_KEY),
        expiresAt: localExpiryMs(),
      });
    }
    if (inFlight) {
      return inFlight;
    }

    inFlight = fetch(apiBaseUrl() + "/checkout/shopflo-auth", {
      method: "GET",
      credentials: "include",
      headers: authHeaders(),
    })
      .then(function (res) {
        if (res.status === 401 || res.status === 403) {
          clearUnavailableShopfloSsoState();
          clearSurmaSessionFlags();
          return { status: "unauthenticated", auth: null, expiresAt: null };
        }
        if (!res.ok) {
          throw new Error("shopflo_auth_http_" + res.status);
        }
        return res.json().then(function (body) {
          var capability = decodeShopfloSsoCapability(body);
          confirmSurmaSession();
          if (capability.status === "ok") {
            lastHydrateAt = Date.now();
            writeRefresh(capability.auth, capability.expiresAt);
            return capability;
          }
          clearUnavailableShopfloSsoState();
          return capability;
        });
      })
      .catch(function (err) {
        console.warn("[surma-checkout] shopflo-auth hydrate failed", err);
        // Transient: keep existing local token if still usable
        if (hasUsableLocalRefresh()) {
          return {
            status: "ok",
            auth: readLocal(FLO_REFRESH_KEY),
            expiresAt: localExpiryMs(),
          };
        }
        return { status: "error", auth: null, expiresAt: null };
      })
      .then(function (result) {
        inFlight = null;
        return result;
      });

    return inFlight;
  }

  function withTimeout(promise, ms) {
    return new Promise(function (resolve) {
      var settled = false;
      var timer = setTimeout(function () {
        if (settled) return;
        settled = true;
        resolve({ status: "timeout", auth: null, expiresAt: null });
      }, ms);
      promise.then(
        function (value) {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          resolve(value);
        },
        function () {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          resolve({ status: "error", auth: null, expiresAt: null });
        },
      );
    });
  }

  /**
   * Ensure local refresh is usable before cart/checkout open.
   * Always resolves quickly so Shopflo SDK can open (SSO or guest).
   */
  function ensureFreshRefresh() {
    if (hasUsableLocalRefresh()) {
      return Promise.resolve({
        status: "ok",
        auth: readLocal(FLO_REFRESH_KEY),
        expiresAt: localExpiryMs(),
      });
    }
    var force = !!readLocal(FLO_REFRESH_KEY) && isLocalRefreshExpired();
    return withTimeout(hydrateShopfloRefresh(force || !readLocal(FLO_REFRESH_KEY)), ENSURE_TIMEOUT_MS);
  }

  // Boot hydrate (non-blocking)
  hydrateShopfloRefresh(false);

  document.addEventListener(
    "click",
    function (event) {
      var t = event.target;
      if (!t || !t.closest) return;
      if (
        t.closest("#checkout2") ||
        t.closest("#flo-buy-now-button") ||
        t.closest(".m-cart-icon-bubble") ||
        t.closest("[name='flo-buy-now-button']") ||
        t.closest(".m-add-to-cart")
      ) {
        ensureFreshRefresh();
      }
    },
    true,
  );

  window.addEventListener("surma:identity-changed", function () {
    if (readLocal(WEB_SESSION_KEY)) hydrateShopfloRefresh(true);
    else clearUnavailableShopfloSsoState();
  });

  window.SurmaCheckout = {
    hydrateShopfloRefresh: hydrateShopfloRefresh,
    ensureFreshRefresh: ensureFreshRefresh,
    hasUsableLocalRefresh: hasUsableLocalRefresh,
  };
})();
